"use client";

import { SignOutButton } from "@clerk/nextjs";
import Link from "next/link";
import { useState } from "react";

import { css } from "@/../styled-system/css";
import CircleHelp from "@/assets/svg/circle-help.svg";
import DollarSign from "@/assets/svg/dollar-sign.svg";
import Heart from "@/assets/svg/heart.svg";
import LayoutGrid from "@/assets/svg/layout-grid.svg";
import Logout from "@/assets/svg/logout.svg";
import HamburgerMenu from "@/assets/svg/menu.svg";
import Package from "@/assets/svg/package.svg";
import ShoppingBag from "@/assets/svg/shopping-bag.svg";
import UserIcon from "@/assets/svg/user.svg";

import ProfileHeader from "./ProfileHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/Dropdown";

const navMenuItems = [
  {
    title: "Catalog",
    key: "catalog",
    url: "/catalog",
    restricted: true,
    icon: ShoppingBag,
  },
  {
    title: "About Us",
    key: "about-us",
    url: "/about-us",
    restricted: false,
    icon: Heart,
  },
  {
    title: "FAQ",
    key: "faq",
    url: "/faqs",
    restricted: false,
    icon: CircleHelp,
  },
  {
    title: "Pricing",
    key: "pricing",
    url: "/pricing",
    restricted: false,
    icon: DollarSign,
  },
];

const accountMenuItems = [
  {
    title: "Profile",
    key: "profile",
    url: "/profile",
    icon: UserIcon,
  },
  {
    title: "Digital Closet",
    key: "digital-closet",
    url: "/digital-closet",
    icon: LayoutGrid,
  },
  {
    title: "Orders",
    key: "orders",
    url: "/order-history",
    icon: Package,
  },
];

const navMenuContainerClass = css({
  transition: "transform 300ms ease",
  transform: "translateX(0)",
  position: "relative",
  width: "100%",
  height: "100%",
  '&[data-state="inactive"]': {
    transform: "translateX(-100%)",
    position: "absolute",
  },
});

const accountMenuContainerClass = css({
  transition: "transform 300ms ease",
  transform: "translateX(305px)",
  position: "absolute",
  width: "100%",
  top: 0,
  left: 0,
  '&[data-state="active"]': {
    transform: "translateX(0)",
    position: "relative",
  },
});

interface NavMenuProps {
  isSignedIn: boolean | undefined;
}

function NavMenu({ isSignedIn }: NavMenuProps) {
  const [isSubMenuActive, setIsSubMenuActive] = useState(false);

  const handleSubMenuToggle = () => {
    setIsSubMenuActive(!isSubMenuActive);
  };

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        if (open === true) {
          setIsSubMenuActive(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          className={css({
            cursor: "pointer",
            display: "block",
            width: "fit",
            md: { display: "none" },
          })}
          aria-label="Navigation Menu"
        >
          <HamburgerMenu width={48} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        css={{
          position: "relative",
          overflow: "hidden",
          width: "305px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ProfileHeader />
        <DropdownMenuSeparator />

        <div className={css({ position: "relative", overflow: "hidden" })}>
          <div
            data-state={isSubMenuActive ? "inactive" : "active"}
            className={navMenuContainerClass}
          >
            {isSignedIn && (
              <button
                onClick={handleSubMenuToggle}
                className={css({ width: "100%" })}
              >
                <DropdownMenuItem
                  css={{ gap: "3" }}
                  onSelect={(event) => event.preventDefault()}
                >
                  <UserIcon width="2rem" />
                  Account Menu
                </DropdownMenuItem>
              </button>
            )}

            {navMenuItems
              .filter((item) => isSignedIn || !item.restricted)
              .map((item) => (
                <Link href={item.url} key={item.title} prefetch>
                  <DropdownMenuItem css={{ gap: "3" }}>
                    <item.icon width="2rem" />
                    {item.title}
                  </DropdownMenuItem>
                </Link>
              ))}
          </div>

          {isSignedIn && (
            <div
              data-state={isSubMenuActive ? "active" : "inactive"}
              className={accountMenuContainerClass}
            >
              <button
                onClick={handleSubMenuToggle}
                className={css({ width: "100%" })}
              >
                <DropdownMenuItem
                  css={{ gap: "3" }}
                  onSelect={(event) => event.preventDefault()}
                >
                  <HamburgerMenu width="2rem" />
                  Main Menu
                </DropdownMenuItem>
              </button>
              {accountMenuItems.map((item) => (
                <Link href={item.url} key={item.title} prefetch>
                  <DropdownMenuItem css={{ gap: "3" }}>
                    <item.icon width="2rem" />
                    {item.title}
                  </DropdownMenuItem>
                </Link>
              ))}
            </div>
          )}
        </div>
        {isSignedIn && (
          <>
            <DropdownMenuSeparator />
            <SignOutButton>
              <DropdownMenuItem css={{ gap: "3" }}>
                <Logout width="2rem" />
                Sign Out
              </DropdownMenuItem>
            </SignOutButton>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NavMenu;
